import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, Search, Kanban, Download,
  Linkedin, Settings, Zap, X, Boxes, Users as UsersIcon, CalendarDays,
  Factory, Eye, FileText, StickyNote, ChevronsLeft, ChevronsRight, Navigation, Rocket, Contact
} from 'lucide-react'
import clsx from 'clsx'
import { useI18n } from '../i18n'

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, key: 'nav.dashboard' },
  { to: '/search', icon: Search, key: 'nav.search' },
  { to: '/crm', icon: Kanban, key: 'nav.crm' },
  { to: '/contacts-linkedin', icon: Contact, key: 'nav.leads' },
  { to: '/agenda', icon: CalendarDays, key: 'nav.agenda' },
  { to: '/tournee', icon: Navigation, key: 'nav.tour' },
  { to: '/notes', icon: StickyNote, key: 'nav.notes' },
  { to: '/report', icon: FileText, key: 'nav.report' },
  { to: '/clients-bydemes', icon: Boxes, key: 'nav.bydemes', adminOnly: true },
  { to: '/export', icon: Download, key: 'nav.export' },
  { to: '/linkedin', icon: Linkedin, key: 'nav.linkedin' },
  { to: '/fournisseurs', icon: Factory, key: 'nav.suppliers' },
  { to: '/veille-linkedin', icon: Eye, key: 'nav.watchlist' },
  { to: '/admin-users', icon: UsersIcon, key: 'nav.users', adminOnly: true },
  { to: '/admin-deploy', icon: Rocket, key: 'nav.deploy', adminOnly: true },
  { to: '/settings', icon: Settings, key: 'nav.settings' },
]

export default function Sidebar({ open = false, onClose, collapsed = false, onToggleCollapsed }) {
  const { t } = useI18n()
  return (
    <>
      {/* Overlay mobile */}
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-30"
          onClick={onClose}
        />
      )}

      <aside
        className={clsx(
          'flex-shrink-0 bg-dark-900 border-r border-dark-700 flex flex-col h-full z-40 transition-all duration-200',
          collapsed ? 'w-16' : 'w-64',
          // Mobile: fixed drawer, slides in/out (force largeur normale en mobile)
          'fixed md:static inset-y-0 left-0',
          open ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          // Force largeur 64 en drawer mobile même si collapsed sur desktop
          open && collapsed ? 'w-64 md:w-16' : ''
        )}
      >
        {/* Logo + close button */}
        <div className={clsx(
          'border-b border-dark-700 flex items-center justify-between',
          collapsed ? 'p-3 justify-center' : 'p-5'
        )}>
          {(!collapsed || open) ? (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center shadow-lg shadow-primary-600/30 flex-shrink-0">
                <Zap className="w-5 h-5 text-dark-50" />
              </div>
              <div className={clsx(collapsed && !open ? 'hidden' : '')}>
                <h1 className="text-dark-50 font-bold text-lg leading-none">ProspectFlow</h1>
                <p className="text-dark-400 text-xs mt-0.5">{t('nav.tagline')}</p>
              </div>
            </div>
          ) : (
            <div className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center shadow-lg shadow-primary-600/30">
              <Zap className="w-5 h-5 text-dark-50" />
            </div>
          )}
          <button
            onClick={onClose}
            className="md:hidden p-1 text-dark-400 hover:text-dark-50"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className={clsx('flex-1 space-y-1 overflow-y-auto', collapsed && !open ? 'p-2' : 'p-3')}>
          {navItems.filter(item => !item.adminOnly || localStorage.getItem('is_admin') === '1').map(({ to, icon: Icon, key }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              title={collapsed && !open ? t(key) : undefined}
              className={({ isActive }) =>
                clsx(
                  'flex items-center rounded-lg transition-all duration-150 group',
                  collapsed && !open ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5',
                  isActive
                    ? 'bg-primary-600/20 text-primary-400 border border-primary-600/30'
                    : 'text-dark-400 hover:text-dark-100 hover:bg-dark-800'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={clsx(
                      'flex-shrink-0 transition-colors',
                      isActive ? 'text-primary-400' : 'text-dark-500 group-hover:text-dark-300'
                    )}
                    size={18}
                  />
                  {(!collapsed || open) && <span className="text-sm font-medium">{t(key)}</span>}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Toggle collapse — desktop seulement */}
        <div className={clsx('border-t border-dark-700', collapsed ? 'p-2' : 'p-3')}>
          <button
            onClick={onToggleCollapsed}
            className="hidden md:flex w-full items-center justify-center gap-2 py-1.5 rounded-md text-dark-500 hover:text-dark-200 hover:bg-dark-800 transition-colors text-xs"
            title={collapsed ? 'Déplier (Ctrl+B)' : 'Réduire (Ctrl+B)'}
          >
            {collapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={14} /> Réduire</>}
          </button>
          {(!collapsed || open) && (
            <p className="text-dark-500 text-xs text-center mt-1">v1.0.0</p>
          )}
        </div>
      </aside>
    </>
  )
}
